import { Clock, MapPin } from 'lucide-react';
import { getSmartDateDisplay } from '@/lib/dateUtils';
import { getEventEmoji, getEventTypeLabel } from '@/lib/eventUtils';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { useUniversalContacts } from '@/hooks/useUniversalContacts';
import { MultipleAssigneeAvatars } from '@/components/tasks/MultipleAssigneeAvatars';
import { useAuth } from '@/hooks/useAuth';

interface EventPreview {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  event_type: string;
  location?: string;
  linked_persons?: string[];
}

interface EventListItemProps {
  event: EventPreview;
  onEventClick: () => void;
}

export function EventListItem({ event, onEventClick }: EventListItemProps) {
  const { openModal } = useModalNavigation();
  const { contacts } = useUniversalContacts();
  const { user } = useAuth();

  const smartDate = getSmartDateDisplay(event.start_date);
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const eventEmoji = getEventEmoji(event.event_type as any);
  const eventTypeLabel = getEventTypeLabel(event.event_type as any);

  // Helper function to detect all-day events
  const isAllDayEvent = (start: Date, end: Date) => {
    // Check if start is 00:00 and end is 23:59 (in UTC or local)
    const startUTC = { hours: start.getUTCHours(), minutes: start.getUTCMinutes() };
    const endUTC = { hours: end.getUTCHours(), minutes: end.getUTCMinutes() };
    
    return (startUTC.hours === 0 && startUTC.minutes === 0) &&
           (endUTC.hours === 23 && endUTC.minutes === 59);
  };

  // Helper function to detect multi-day events (excluding all-day events on same day)
  const isMultiDayEvent = (start: Date, end: Date) => {
    // Check if it's the same day in UTC
    const sameDay = start.getUTCFullYear() === end.getUTCFullYear() &&
                    start.getUTCMonth() === end.getUTCMonth() &&
                    start.getUTCDate() === end.getUTCDate();
    
    // If it's all-day event on same day, it's not multi-day
    if (sameDay && isAllDayEvent(start, end)) {
      return false;
    }
    
    return start.toDateString() !== end.toDateString();
  };

  // Get linked contacts for avatars - filter contacts by linked_persons IDs and exclude current user
  const linkedContacts = contacts.filter(contact => {
    const isLinked = event.linked_persons?.includes(contact.originalData.id) || false;
    const isCurrentUser = contact.originalData.profile_id === user?.id;
    return isLinked && !isCurrentUser;
  });

  const handleClick = () => {
    openModal('viewEvent', { id: event.id });
  };

  return (
    <>
      <div 
        className="relative w-full border border-panel rounded-lg p-3 bg-panel hover:bg-panel-hover cursor-pointer transition-all duration-200 group"
        onClick={handleClick}
      >
        <div className="flex items-start justify-between">
          {/* Left: Event content */}
          <div className="flex-1 min-w-0 space-y-1 pr-3">
            <div className="mb-1">
              <h4 className="font-medium text-sm leading-tight text-foreground break-words line-clamp-2">
                {event.event_type !== 'birthday' && eventEmoji} {event.title}
              </h4>
            </div>
          
            {/* Date and time on separate lines */}
            <div className="space-y-1">
              {isMultiDayEvent(startDate, endDate) ? (
                <div className="text-xs">
                  <span className="text-primary font-medium">
                    Du {startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au {endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              ) : smartDate && (
                <div className="text-xs">
                  <span className={`${
                    smartDate === "Hier" || smartDate.startsWith("Il y a") ? 'text-muted-foreground' :
                    smartDate === "Aujourd'hui" ? 'text-primary font-medium' :
                    smartDate === "Demain" ? 'text-primary' :
                    'text-muted-foreground'
                  }`}>
                    {smartDate}
                  </span>
                </div>
              )}
              
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {/* Afficher l'heure seulement si ce n'est pas un événement toute la journée */}
                {!isAllDayEvent(startDate, endDate) && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>
                      {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      {isMultiDayEvent(startDate, endDate) && (
                        <> - {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false })}</>
                      )}
                    </span>
                  </div>
                )}
                
                {event.location && (
                  <div className="flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" />
                    <span className="truncate">{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Avatars */}
          {linkedContacts.length > 0 && (
            <div className="flex-shrink-0">
              <MultipleAssigneeAvatars assignedContacts={linkedContacts} size="sm" maxVisible={3} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}